import { ChangeDetectionStrategy, Component } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ChanceNodeDto, TriggerDto, UserDto } from "@api";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { AppRoutes, CHANCE_NODES_PER_PAGE, TOAST_DEFAULT_DURATION } from "src/app/constants";
import { RootState } from "src/app/store";
import * as FromUser from "src/app/store/user/selectors";
import * as FromScenario from "src/app/store/scenario/selectors";
import * as ScenarioActions from "src/app/store/scenario/actions";
import { PopoverButton, PopoverComponent } from "src/app/components/popover/popover.component";
import { AlertController, IonRouterOutlet, ModalController, NavController, PopoverController, ToastController } from "@ionic/angular";
import { ScenarioPage } from "src/app/modals/scenario/scenario.page";
import { TranslateService } from "@ngx-translate/core";
import { first } from "rxjs/operators";
import { EnvironmentService } from "src/app/services/environment/environment.service";

@Component({
  selector: "app-scenarios",
  templateUrl: "./scenarios.page.html",
  styleUrls: ["./scenarios.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenariosPage {
  public routes: typeof AppRoutes = AppRoutes;
  public triggerTypes: typeof TriggerDto.TypeEnum = TriggerDto.TypeEnum;
  public form: FormGroup;
  public user$: Observable<UserDto> = this.store.select(FromUser.selectUser);
  public scenarios$: Observable<ChanceNodeDto[]> = this.store.select(FromScenario.getScenarios);
  public breadcrumbs = [
    { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${this.routes.home}` },
    { name: this.i18n.instant("SCENARIOS.BREADCRUMB_NAME"), link: `/${this.routes.scenarios}` },
  ];

  constructor(
    private store: Store<RootState>,
    private environmentService: EnvironmentService,
    private i18n: TranslateService,
    private navController: NavController,
    private routerOutlet: IonRouterOutlet,
    private modalController: ModalController,
    private alertController: AlertController,
    private popoverController: PopoverController,
    private toastController: ToastController,
  ) {
    this.form = new FormGroup({ search: new FormControl("") });
  }

  public trackBy(_: number, scenario: ChanceNodeDto): string {
    return scenario.id;
  }

  public async presentGeneralPopover(event: Event): Promise<void> {
    const buttons: PopoverButton[] = [{ text: this.i18n.instant("BUTTONS.ADD"), handler: (): Promise<void> => this.addScenario() }];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  public presentScenarioPopover(event: Event, scenario: ChanceNodeDto): void {
    event.stopPropagation();
    this.store
      .select(FromScenario.getTrigger(scenario.triggerId))
      .pipe(first())
      .subscribe(async trigger => {
        const buttons: PopoverButton[] = [
          {
            text: this.i18n.instant("BUTTONS.VIEW"),
            handler: (): Promise<boolean> => this.navController.navigateForward([AppRoutes.scenarios, scenario.id]),
          },
          { text: this.i18n.instant("BUTTONS.EDIT"), handler: (): Promise<void> => this.editScenario(scenario) },
          trigger.type === TriggerDto.TypeEnum.Webhook
            ? { text: this.i18n.instant("BUTTONS.COPY_ADDRESS"), handler: (): Promise<void> => this.copyWebhookUrl(scenario) }
            : undefined,
          { text: this.i18n.instant("BUTTONS.DELETE"), handler: (): Promise<void> => this.deleteScenario(scenario), danger: true },
        ].filter(Boolean);
        const popover = await this.popoverController.create({
          component: PopoverComponent,
          componentProps: { buttons },
          event,
        });

        await popover.present();
      });
  }

  public async viewScenario(scenario: ChanceNodeDto): Promise<boolean> {
    return this.navController.navigateForward([AppRoutes.scenarios, scenario.id]);
  }

  public async copyWebhookUrl(scenario: ChanceNodeDto): Promise<void> {
    const toast = await this.toastController.create({
      duration: TOAST_DEFAULT_DURATION,
      color: "dark",
      message: this.i18n.instant("SCENARIOS.COPIED_TO_CLIPBOARD"),
      icon: "copy-outline",
    });

    await navigator.clipboard
      ?.writeText(`${this.environmentService.get("apiPath")}/public/chance-nodes/${scenario.id}/run`)
      .catch(async error => {
        const toastError = await this.toastController.create({
          duration: TOAST_DEFAULT_DURATION,
          color: "danger",
          message: error.message,
        });
        await toastError.present();
      });

    await toast.present();
  }

  protected ionViewWillEnter(): void {
    this.store.dispatch(ScenarioActions.getChanceNodes({ limit: CHANCE_NODES_PER_PAGE, page: 1 }));
    this.store.dispatch(ScenarioActions.getTriggers());
    this.store.dispatch(ScenarioActions.getActions());
  }

  private async addScenario(): Promise<void> {
    const modal = await this.modalController.create({
      component: ScenarioPage,
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  private async editScenario(scenario: ChanceNodeDto): Promise<void> {
    const modal = await this.modalController.create({
      component: ScenarioPage,
      componentProps: { scenario },
      showBackdrop: true,
      presentingElement: this.routerOutlet.nativeEl,
      swipeToClose: true,
    });

    await modal.present();
  }

  private async deleteScenario(scenario: ChanceNodeDto): Promise<void> {
    const alert = await this.alertController.create({
      header: this.i18n.instant("SCENARIOS.DELETE_MODAL.HEADER"),
      message: this.i18n.instant("SCENARIOS.DELETE_MODAL.MESSAGE"),
      buttons: [
        { text: this.i18n.instant("BUTTONS.CANCEL"), role: "cancel" },
        {
          text: this.i18n.instant("BUTTONS.CONFIRM_DELETE"),
          handler: (): void => this.store.dispatch(ScenarioActions.deleteChanceNode({ chanceNodeId: scenario.id })),
          role: "destructive",
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
  }
}
